import { Box, Button, styled, useTheme } from '@mui/material';
import { ShowInactiveToggle } from '../../../components/ShowInactiveToggle/ShowInactiveToggle';
import { ColumnFiltersState, Table } from '@tanstack/react-table';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Multiselect } from '../../../components/Multiselect/Multiselect';
import { Typography } from '../../../components/Typography/Typography';
import {
  tsStatuesSelectedOPs,
  tsStatusesRangeEnd,
  tsStatusesRangeStart,
  tsStatusesSubmitterLabel,
  tsStatusesShowInactive,
} from '../../../state/UIAccountantState';
import { SelectItem, TsRangeTypeItems, UserWithTsPeriodData } from '../../../types';
import { isSmallScreenState } from '../../../state/UIState';
import { SingleSelect } from '../../../components/SingleSelect/SingleSelect';
import {
  MonthlyRangeSelector,
  MonthYear,
} from '../../../components/MonthlyRangeSelector/MonthlyRangeSelector';
import { useFilteredOpsTimesheets } from '../../../queries/hooks/useFilteredOpsTimesheets';
import { format } from 'date-fns';
import { getStartDateFromMonthOffset } from '../../../utils/formatters';

type Props = {
  columnFilters: ColumnFiltersState;
  table: Table<UserWithTsPeriodData>;
};

export function FilterSection({ table, columnFilters }: Props) {
  const { colors } = useTheme();
  const location = useLocation();
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const navigate = useNavigate();
  const today = useMemo(() => {
    return new Date();
  }, []);

  const [selectedOPs, setSelectedOPs] = useAtom(tsStatuesSelectedOPs);
  const [showInactive, setShowInactive] = useAtom(tsStatusesShowInactive);
  const submitterLabel = useAtomValue(tsStatusesSubmitterLabel);
  const [selectedRange, setSelectedRange] = useState<SelectItem | null>(null);
  const [isMonthlyRangePopupOpen, setIsMonthlyRangePopupOpen] = useState(false);
  const [rangeOptionsOpen, isRangeOptionsOpen] = useState(false);

  const [selectedRangeStart, setSelectedRangeStart] = useAtom(tsStatusesRangeStart);
  const [selectedRangeEnd, setSelectedRangeEnd] = useAtom(tsStatusesRangeEnd);

  const parseStringToMonthYear = useCallback((dateString: string): MonthYear | undefined => {
    if (!dateString) return undefined;
    const [year, month] = dateString.split('-').map(Number);
    return { year, month: month - 1 }; 
  }, []);

  const currentStartDate = useMemo(() => {
    if (selectedRange?.id === 1) {
      return parseStringToMonthYear(selectedRangeStart);
    }
    return undefined;
  }, [selectedRangeStart, parseStringToMonthYear, selectedRange]);

  const currentEndDate = useMemo(() => {
    if (selectedRange?.id === 1) {
      return parseStringToMonthYear(selectedRangeEnd);
    }
    return undefined;
  }, [selectedRangeEnd, parseStringToMonthYear, selectedRange]);

  const formatDateRange = useCallback((startDate: string, endDate: string) => {
    if (!startDate || !endDate) return '';
    const start = new Date(startDate + '-01');
    const end = new Date(endDate + '-01');
    return `${format(start, 'MMM yyyy')} - ${format(end, 'MMM yyyy')}`;
  }, []);

  const displayedSelectedRange = useMemo(() => {
    if (selectedRange?.id === 1 && selectedRangeStart && selectedRangeEnd) {
      return {
        ...selectedRange,
        value: formatDateRange(selectedRangeStart, selectedRangeEnd),
      };
    }
    return selectedRange;
  }, [selectedRange, selectedRangeStart, selectedRangeEnd, formatDateRange]);

  // Same query (all statuses) as the table, so this is served from cache.
  const { data: operatingPartners } = useFilteredOpsTimesheets(
    selectedRangeStart,
    selectedRangeEnd,
    undefined,
    submitterLabel
  );

  const inactiveCount = useMemo(
    () => (operatingPartners ?? []).filter((op) => op.isActive === false).length,
    [operatingPartners]
  );

  useEffect(() => {
    setSelectedRange(TsRangeTypeItems[0]);
  }, []);

  const sortedOPList = useMemo(
    () => {
      if (!operatingPartners) return [];
      const allOPs = operatingPartners
        .filter((user) => showInactive || user.isActive !== false)
        .map((user) => ({ id: user.id, value: user.name }));
      return allOPs;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table, operatingPartners, showInactive]
  );

  const onChangeOPFilter = useCallback(
    (OPs: SelectItem[] | undefined) => {
      if (OPs) {
        setSelectedOPs(OPs);
        table.getColumn('name')?.setFilterValue(OPs);
      }
    },
    [table, setSelectedOPs]
  );

  const onFiltersReset = useCallback(() => {
    setSelectedOPs([]);
    setSelectedRange(TsRangeTypeItems[0]);
    setShowInactive(false);
    const formatedRangeStart = format(getStartDateFromMonthOffset(today, 6), 'yyyy-MM');
    setSelectedRangeStart(formatedRangeStart);
    setSelectedRangeEnd(format(today, 'yyyy-MM'));
    navigate(location.pathname);
  }, [
    setSelectedOPs,
    setSelectedRange,
    setShowInactive,
    navigate,
    location,
    setSelectedRangeStart,
    setSelectedRangeEnd,
    today,
  ]);

  const isResetDisabled = useMemo(() => {
    const selectedOPs = columnFilters.find((filter) => filter.id === 'name')?.value as SelectItem[];
    if (selectedOPs?.length === 0 && selectedRange?.id === 6 && !showInactive) {
      return true;
    }

    return false;
  }, [columnFilters, selectedRange?.id, showInactive]);

  useEffect(() => {
    table.getColumn('name')?.setFilterValue(selectedOPs);
  }, [selectedOPs, table]);

  return (
    <Container isSmallScreen={isSmallScreen}>
      <Box flexGrow={isSmallScreen ? '1' : '0'}>
        <Multiselect
          style={{
            width: isSmallScreen ? 'auto' : '200px',
            marginRight: isSmallScreen ? '4px' : '14px',
          }}
          fieldPlaceholder={isSmallScreen ? 'Submitter' : 'Select submitter'}
          options={sortedOPList as SelectItem[]}
          onChange={(_, OPs) => onChangeOPFilter(OPs)}
          value={selectedOPs}
        />
      </Box>
      <Box flexGrow={isSmallScreen ? '1' : '0'}>
        <SingleSelect
          options={TsRangeTypeItems}
          onOpen={() => {
            isRangeOptionsOpen(true);
            if (selectedRange?.id === 1) {
              setIsMonthlyRangePopupOpen(true);
            }
          }}
          onClose={() => {
            isRangeOptionsOpen(false);
          }}
          onChange={(_, value) => {
            if (!value) return;
            else {
              setSelectedRange(value);
              if (value.id !== 1) {
                const formatedRangeStart = format(
                  getStartDateFromMonthOffset(today, Number(value.id)),
                  'yyyy-MM'
                );
                setSelectedRangeStart(formatedRangeStart);
                setSelectedRangeEnd(format(today, 'yyyy-MM'));
              } else {
                setIsMonthlyRangePopupOpen(true);
              }
            }
          }}
          value={displayedSelectedRange}
          hideClearIcon
          open={rangeOptionsOpen || isMonthlyRangePopupOpen}
          style={{
            width: '225px'
          }}
        />
      </Box>
      <MonthlyRangeSelector
        open={isMonthlyRangePopupOpen}
        startDate={currentStartDate}
        endDate={currentEndDate}
        handleOnClose={() => setIsMonthlyRangePopupOpen(false)}
        onRangeChange={(start, end) => {
          if (start && end) {
            setSelectedRangeStart(
              `${start?.year}-${(start?.month + 1).toString().padStart(2, '0')}`
            );
            setSelectedRangeEnd(`${end?.year}-${(end?.month + 1).toString().padStart(2, '0')}`);
            setIsMonthlyRangePopupOpen(false);
          }
        }}
      />
      <Box flexGrow={isSmallScreen ? '1' : '0'} display='flex' alignItems='center'>
        <ShowInactiveToggle
          checked={showInactive}
          onChange={setShowInactive}
          label='Show inactive'
          count={inactiveCount}
        />
      </Box>
      <Box flexGrow={isSmallScreen ? '1' : '0'}>
        <Button
          onClick={onFiltersReset}
          variant='text'
          style={{ height: '28px', marginTop: '2px' }}
          disabled={isResetDisabled}
          sx={{
            '.MuiButton-startIcon': { marginRight: '2px' },
            '&.Mui-disabled': { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'auto' },
          }}
        >
          <Typography variant='subtitle1' color={colors.textAccent.default}>
            {isSmallScreen ? 'Reset' : 'Reset Filters'}
          </Typography>
        </Button>
      </Box>
    </Container>
  );
}

const Container = styled('div')<{ isSmallScreen: boolean }>`
  display: flex;
  align-items: center;
  margin-bottom: ${({ isSmallScreen }) => (isSmallScreen ? '10px' : '20px')};
  gap: ${({ isSmallScreen }) => (isSmallScreen ? '0px' : '12px')};
`;
